/**
 * Client-side CSV export — turning whatever rows a screen is already
 * showing (filtered, sorted, as the viewer currently has it) into a file
 * they can open elsewhere. Extracted from the two report screens that
 * already did this locally (`DailyReportTeam.tsx`, `DailyReportPerson.tsx`)
 * so every other screen wiring up an "Export CSV" button uses the same
 * quoting rule rather than a fourth or fifth copy of it.
 */

import { saveBlob } from "./transport.js";

/** A leading `= + - @`, tab or carriage return makes Excel and Sheets read the
 *  cell as a formula. */
const FORMULA_LEAD = /^[=+\-@\t\r]/;

/** Wraps a value in quotes and doubles any embedded quote, per RFC 4180 —
 *  the minimum needed so a comma or quote inside a cell does not silently
 *  shift every column after it.
 *
 *  A task titled `=HYPERLINK(...)` would otherwise run when the file is opened
 *  (CSV injection), so a leading formula character gets a single quote in front
 *  — the OWASP-recommended neutraliser, shown as plain text by spreadsheets. */
export function csvCell(value: string): string {
  const safe = FORMULA_LEAD.test(value) ? `'${value}` : value;
  return `"${safe.replace(/"/g, '""')}"`;
}

/** Builds CSV text from a header row and a per-row cell mapper. */
export function toCsv<T>(headers: string[], rows: T[], toValues: (row: T) => string[]): string {
  const lines = rows.map((row) => toValues(row).map(csvCell).join(","));
  return [headers.map(csvCell).join(","), ...lines].join("\r\n");
}

/** The file itself. Led by a UTF-8 byte-order mark (written as an escape, since
 *  the character is invisible in a diff): without it Excel opens the file as
 *  Windows-1252 and every accented name or non-Latin character turns to noise. */
export function csvBlob(content: string): Blob {
  return new Blob(["\uFEFF", content], { type: "text/csv;charset=utf-8" });
}

/** Saves `content` as a named file — a browser download, or Downloads on desktop. */
export function downloadCsv(fileName: string, content: string): void {
  void saveBlob(csvBlob(content), fileName);
}
